
import { config } from "@/config";

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface GeocodeResult {
  address_components: AddressComponent[];
  formatted_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
}

// 從地址組件中移除街道號碼
export const removeStreetNumber = (addressComponents: AddressComponent[]): AddressComponent[] => {
  return addressComponents.filter(component => 
    !component.types.includes('street_number')
  );
};

// 重新組合地址字串
export const formatAddressFromComponents = (components: AddressComponent[]): string => {
  const addressParts: string[] = [];
  
  components.forEach(component => {
    if (component.types.includes('street_number')) {
      // 跳過街道號碼
      return;
    } else if (component.types.includes('route')) {
      addressParts.unshift(component.long_name); // 街道名稱放在最前面
    } else if (component.types.includes('locality')) {
      addressParts.push(component.long_name); // 城市
    } else if (component.types.includes('administrative_area_level_1')) {
      addressParts.push(component.short_name); // 省份縮寫
    } else if (component.types.includes('country')) {
      addressParts.push(component.short_name); // 國家縮寫
    } else if (component.types.includes('postal_code')) {
      // 可選：是否要包含郵遞區號
    }
  });
  
  return addressParts.join(', ');
};

// 使用 Google Geocoding API 查詢地址座標
export const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
  try {
    const encodedAddress = encodeURIComponent(address);
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${config.googleMapsApiKey}`
    );
    
    if (!response.ok) {
      throw new Error('Geocoding API request failed');
    }
    
    const data = await response.json();
    
    if (data.status === 'OK' && data.results.length > 0) {
      const location = data.results[0].geometry.location;
      return {
        lat: typeof location.lat === 'function' ? location.lat() : location.lat,
        lng: typeof location.lng === 'function' ? location.lng() : location.lng
      };
    }
    
    console.error('Geocoding failed:', data.status, data.error_message);
    return null;
  } catch (error) {
    console.error('Error in geocoding:', error);
    return null;
  }
};

// 處理隱私地址邏輯
export const processPrivacyAddress = async (
  fullAddress: string,
  fullCoordinates: { lat: number; lng: number },
  hideStreetNumber: boolean,
  addressComponents?: AddressComponent[]
): Promise<{
  displayAddress: string;
  displayCoordinates: { lat: number; lng: number };
}> => {
  console.log("processPrivacyAddress called:", { fullAddress, hideStreetNumber });
  
  if (!hideStreetNumber) {
    console.log("Not hiding street number, returning original address");
    return {
      displayAddress: fullAddress,
      displayCoordinates: fullCoordinates
    };
  }

  try {
    // 如果有地址組件，直接處理
    if (addressComponents) {
      console.log("Processing with address components");
      const filteredComponents = removeStreetNumber(addressComponents);
      const displayAddress = formatAddressFromComponents(filteredComponents);
      const displayCoordinates = await geocodeAddress(displayAddress);
      
      return {
        displayAddress,
        displayCoordinates: displayCoordinates || fullCoordinates
      };
    }

    // 如果沒有地址組件，嘗試從完整地址中移除門牌號碼
    console.log("Processing without address components");
    const addressParts = fullAddress.split(',');
    if (addressParts.length > 0) {
      // 移除第一部分的門牌號碼（假設格式為 "123 Street Name"）
      const firstPart = addressParts[0].trim();
      console.log("First part:", firstPart);
      
      const streetWithoutNumber = firstPart.replace(/^\d+\s+/, ''); // 移除開頭的數字和空格
      console.log("Street without number:", streetWithoutNumber);
      
      if (streetWithoutNumber === firstPart) {
        // 如果沒有找到數字開頭，嘗試其他格式
        console.log("No number found at start, trying other patterns");
        return {
          displayAddress: fullAddress,
          displayCoordinates: fullCoordinates
        };
      }
      
      const displayAddress = [streetWithoutNumber, ...addressParts.slice(1)].join(', ');
      console.log("Display address:", displayAddress);
      
      const displayCoordinates = await geocodeAddress(displayAddress);
      
      return {
        displayAddress,
        displayCoordinates: displayCoordinates || fullCoordinates
      };
    }

    // 如果無法處理，返回原始資料
    console.log("Unable to process address, returning original");
    return {
      displayAddress: fullAddress,
      displayCoordinates: fullCoordinates
    };
  } catch (error) {
    console.error('Error processing privacy address:', error);
    return {
      displayAddress: fullAddress,
      displayCoordinates: fullCoordinates
    };
  }
};

// 獲取顯示用房東名稱
export const getDisplayLandlordName = (
  landlordName?: string,
  showLandlordName?: boolean,
  language: string = 'zh-TW'
): string => {
  if (showLandlordName === false) {
    return language === 'en' ? 'Hide host name' : '不顯示房東名稱';
  }
  
  return landlordName || (language === 'en' ? 'Landlord name not provided' : '房東名稱未提供');
};
